import { localizedStrings } from '@core/strings'
import React, { useState } from 'react'
import { SignatureEdit } from 'webapp/components/common/Signature'
import { useModal } from 'webapp/components/common/GlobalModal'
import {
  DialogActions,
  DialogTitle,
  IconButton,
  DialogContent,
  Button,
} from '@common/material'
import { Text } from '@fundamentals'
import ClearIcon from '@mui/icons-material/Clear'
import moment from 'moment'
import { useGetProfile } from '@core/react-query/features/profile/hooks/useGetProfile'
import { Permit } from '@core/entities/permit/Permit'
import { ModalDialogContainer } from '@common/modal'
import { useToast } from '@core/toast'
import { useApproveTransfer } from '@core/react-query/features/permits/permit'
import { Section } from '@common/Section'

interface ComponentType {
  permit: Permit
}

export const ApprovePermitTransferModal: React.FC<ComponentType> = ({
  permit,
}) => {
  const approveTransferPermit = useApproveTransfer()
  const { hideModal } = useModal()
  const { data: profile } = useGetProfile()
  const [error, setError] = useState('')
  const { message, createdOn, sentTo, createdBy } = permit?.lastTransfer
  const datetimeString = moment(createdOn).format('D MMMM YYYY HH:mm')
  const toast = useToast()

  return (
    <ModalDialogContainer>
      <DialogTitle
        variant='h5'
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        sx={{ pr: 1, fontSize: 20 }}
      >
        {localizedStrings.approveTransfer}
        <IconButton aria-label='close-modal' onClick={hideModal}>
          <ClearIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Section label={localizedStrings.intendedRecipient} mt={0.5}>
          <Text>{sentTo?.getFullName()}</Text>
        </Section>

        <Section
          label={`Note from ${createdBy?.getFullName()} - ${datetimeString}`}
          mt={2}
        >
          <Text>{message}</Text>
        </Section>
        <SignatureEdit readonly value={profile?.signatureUrl} />
        <ErrorMessage>{error}</ErrorMessage>
      </DialogContent>

      <DialogActions sx={{ pb: 2, pr: 3 }}>
        <Button variant='outlined' onClick={hideModal}>
          {localizedStrings.cancel}
        </Button>
        <Button
          variant='contained'
          onClick={() => {
            approveTransferPermit.mutate(
              { permitId: permit.id },
              {
                onError: (e) => {
                  setError(e.message)
                  toast.error(e.message)
                },
                onSuccess: () => {
                  toast.success(localizedStrings.transferApproved)
                  hideModal()
                },
              },
            )
          }}
          data-test='permit-transfer-approve'
        >
          {localizedStrings.submit}
        </Button>
      </DialogActions>
    </ModalDialogContainer>
  )
}
